import React, { useEffect, useState } from 'react';

interface LabyrinthProps {
  svgPath?: string;
  size?: number;
  onClick?: () => void;
  rotate?: boolean;
  showLogo?: boolean;
  logoClicked?: boolean;
}

const Labyrinth: React.FC<LabyrinthProps> = ({ 
  svgPath = '/images/labyrinth.svg',
  size = 500,
  onClick,
  rotate = false,
  showLogo = false,
  logoClicked = false
}) => {
  // Determine if we're on a mobile device
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [rotation, setRotation] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [animationDirection, setAnimationDirection] = useState(1); // 1 for clockwise, -1 for counterclockwise
  const [currentSpeed, setCurrentSpeed] = useState(0.1); // Initial rotation speed
  // We use setAnimationTime to track timing but don't need to read the value directly
  const [, setAnimationTime] = useState(0); // Track animation time in ms
  
  // Track whether the SUBSTANS title has appeared
  const [titleAppeared, setTitleAppeared] = useState(false);
  
  // Set titleAppeared to true when showLogo becomes true (SUBSTANS title appears)
  useEffect(() => {
    if (showLogo) {
      setTitleAppeared(true);
    }
  }, [showLogo]);
  
  // Complex rotation animation with phases
  useEffect(() => {
    if (!rotate || !titleAppeared) return;
    
    // Animation phases:
    // 0: Initial slow rotation (10 seconds)
    // 1: Acceleration phase (20 seconds)
    // 2: Deceleration phase (20 seconds)
    // 3: Pause (1 second)
    // 4: Direction change + initial slow rotation (10 seconds)
    // 5: Acceleration phase in opposite direction (20 seconds)
    // 6: Deceleration phase in opposite direction (20 seconds)
    // 7: Pause (1 second) then reset to phase 0
    
    const INITIAL_SPEED = 0.1;
    const MAX_SPEED = 0.25; // Reduced by 50% again (from 0.5 to 0.25)
    // ACCELERATION_RATE no longer needed with our new timing-based approach
    const FRAME_RATE = 16; // ~60fps
    
    // Animation timing constants (in ms)
    const SLOW_ROTATION_TIME = 10000; // 10 seconds of initial slow rotation
    const ACCELERATION_TIME = 20000; // 20 seconds for acceleration phase
    const DECELERATION_TIME = 20000; // 20 seconds for deceleration phase
    const PAUSE_TIME = 5000; // Extended to 5 seconds (from 1 second)
    
    // No longer need to track accumulated rotation as we're using time-based phases
    
    const animationInterval = setInterval(() => {
      setAnimationTime(prevTime => {
        const newTime = prevTime + FRAME_RATE;
        
        // Update animation based on current phase
        switch(animationPhase) {
          case 0: // Initial slow rotation (10 seconds)
            setRotation(prev => prev + (INITIAL_SPEED * animationDirection));
            if (newTime >= SLOW_ROTATION_TIME) {
              setAnimationPhase(1);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 1: // Acceleration phase (20 seconds)
            // Calculate progress through the acceleration phase (0 to 1)
            const accelerationProgress = Math.min(newTime / ACCELERATION_TIME, 1);
            
            // Calculate current speed based on progress (ease-in function)
            const accelerationSpeed = INITIAL_SPEED + (MAX_SPEED - INITIAL_SPEED) * (accelerationProgress * accelerationProgress);
            
            // Apply rotation with current speed
            setRotation(prev => prev + (accelerationSpeed * animationDirection));
            
            // Move to deceleration phase after 20 seconds
            if (newTime >= ACCELERATION_TIME) {
              setCurrentSpeed(MAX_SPEED); // Set to max speed for deceleration phase
              setAnimationPhase(2);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 2: // Deceleration phase (20 seconds)
            // Calculate progress through the deceleration phase (0 to 1)
            const decelerationProgress = Math.min(newTime / DECELERATION_TIME, 1);
            
            // Calculate current speed based on progress (ease-out function)
            const decelerationSpeed = MAX_SPEED - (MAX_SPEED - INITIAL_SPEED) * (decelerationProgress * decelerationProgress);
            
            // Apply rotation with current speed
            setRotation(prev => prev + (decelerationSpeed * animationDirection));
            
            // Move to pause phase after 20 seconds
            if (newTime >= DECELERATION_TIME) {
              setCurrentSpeed(INITIAL_SPEED); // Reset to initial speed
              setAnimationPhase(3);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 3: // Pause (5 seconds)
            // No rotation during pause
            if (newTime >= PAUSE_TIME) {
              setAnimationDirection(prev => prev * -1); // Reverse direction
              setAnimationPhase(4);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 4: // Direction change + initial slow rotation (10 seconds)
            setRotation(prev => prev + (INITIAL_SPEED * animationDirection));
            if (newTime >= SLOW_ROTATION_TIME) {
              setAnimationPhase(5);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 5: // Acceleration phase in opposite direction (20 seconds)
            // Calculate progress through the acceleration phase (0 to 1)
            const reverseAccelerationProgress = Math.min(newTime / ACCELERATION_TIME, 1);
            
            // Calculate current speed based on progress (ease-in function)
            const reverseAccelerationSpeed = INITIAL_SPEED + (MAX_SPEED - INITIAL_SPEED) * (reverseAccelerationProgress * reverseAccelerationProgress);
            
            // Apply rotation with current speed
            setRotation(prev => prev + (reverseAccelerationSpeed * animationDirection));
            
            // Move to deceleration phase after 20 seconds
            if (newTime >= ACCELERATION_TIME) {
              setCurrentSpeed(MAX_SPEED); // Set to max speed for deceleration phase
              setAnimationPhase(6);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 6: // Deceleration phase in opposite direction (20 seconds)
            // Calculate progress through the deceleration phase (0 to 1)
            const reverseDecelerationProgress = Math.min(newTime / DECELERATION_TIME, 1);
            
            // Calculate current speed based on progress (ease-out function)
            const reverseDecelerationSpeed = MAX_SPEED - (MAX_SPEED - INITIAL_SPEED) * (reverseDecelerationProgress * reverseDecelerationProgress);
            
            // Apply rotation with current speed
            setRotation(prev => prev + (reverseDecelerationSpeed * animationDirection));
            
            // Move to pause phase after 20 seconds
            if (newTime >= DECELERATION_TIME) {
              setCurrentSpeed(INITIAL_SPEED); // Reset to initial speed
              setAnimationPhase(7);
              return 0; // Reset timer for next phase
            }
            break;
            
          case 7: // Final pause (5 seconds) then reset to phase 0
            // No rotation during pause
            if (newTime >= PAUSE_TIME) {
              setAnimationDirection(1); // Reset to clockwise
              setAnimationPhase(0);
              return 0; // Reset timer for next phase
            }
            break;
        }
        
        return newTime;
      });
    }, FRAME_RATE);
    
    return () => {
      clearInterval(animationInterval);
    };
  }, [rotate, titleAppeared, animationPhase, animationDirection, currentSpeed]);
  
  return (
    <div 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        width: size,
        height: size,
        position: 'relative',
        maxWidth: '100vw', // Ensure it doesn't overflow on mobile
        maxHeight: isMobile ? '50vh' : 'auto' // Limit height on mobile
      }}
    >
      {/* SVG Labyrinth Image */}
      <img 
        src={svgPath} 
        alt="Labyrinth" 
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'contain',
          transform: rotate ? `rotate(${rotation}deg)` : 'none',
          transition: 'transform 0.016s linear', // Smoother transition at ~60fps
          maxWidth: '100%',
          maxHeight: '100%'
        }} 
      />
      
      {/* Substans Logo in the center with clickable button */}
      {showLogo && !logoClicked && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: size * 0.1, // 10% of the total size
            height: size * 0.1,
            minWidth: '24px', // Ensure minimum size on small screens
            minHeight: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            cursor: 'pointer',
            opacity: 0, // Start with opacity 0
            animation: 'fadeIn 0.5s ease-in forwards' // Half-second fade-in animation
          }}
          onClick={onClick}
        >
          <img 
            src="/substans.svg" 
            alt="Substans Logo" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
    </div>
  );
};

// Add CSS for the fade-in animation
const fadeInStyle = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Add the style to the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = fadeInStyle;
  document.head.appendChild(styleElement);
}

export default Labyrinth;
